import { useState } from "react";

const ITEMS_PER_PAGE = 10;

interface UseMeterPaginationOptions {
  totalCount: number;
  itemsPerPage?: number;
}

export function useMeterPagination({
  totalCount,
  itemsPerPage = ITEMS_PER_PAGE,
}: UseMeterPaginationOptions) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const nextPage = () => goToPage(currentPage + 1);
  const prevPage = () => goToPage(currentPage - 1);

  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const reset = () => setCurrentPage(1);

  return {
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
    reset,
    itemsPerPage,
  };
}

// For managing pagination state across multiple zones
export function useZonePagination() {
  const [zonePages, setZonePages] = useState<Record<string, number>>({});

  const getPage = (zone: string) => zonePages[zone] || 1;

  const setPage = (zone: string, page: number) => {
    setZonePages((prev) => ({ ...prev, [zone]: page }));
  };

  const resetPage = (zone: string) => {
    setZonePages((prev) => ({ ...prev, [zone]: 1 }));
  };

  return { getPage, setPage, resetPage };
}
